import React from "react";

import { Container } from "@brexhq/next/src/components/layout/Container/Container";
import {
  SanityCardProps,
  SanityCardsModuleProps,
} from "@brexhq/next/src/components/modules/CardsModule/types";
import { ImageBorderRadiusWrapper } from "@brexhq/next/src/components/shared/BynderAsset/Image/BynderImage";
import {
  CardMedia,
  CardWrapper,
  FullHeightAndWidthLink,
  IconContainer,
} from "@brexhq/next/src/components/shared/Card/Card.elements";
import { BodyL, TitleS } from "@brexhq/shared/components/Typography/Components";
import { Icon } from "@brexhq/shared/components/deprecated/Icons";
import { ModuleThemeType, isDarkTheme } from "@brexhq/shared/types/ModuleTheme";
import { buildUrlFromSlug } from "@brexhq/shared/utils/buildUrlFromSlug";

import { ArticleData } from "../../../utils/repeatableContent/repeatableContentUtils";
import { ArticleTag } from "../../shared/ArticleTag/ArticleTag";
import { getButtonText } from "../ArticleLinkList/ArticleLinkList";

export interface CardProps extends SanityCardProps {
  minHeight?: SanityCardsModuleProps["data"]["minHeight"];
  verticalAlignment: SanityCardsModuleProps["data"]["verticalAlignment"];
  borderRadius: SanityCardsModuleProps["data"]["borderRadius"];
  customBorderRadius?: SanityCardsModuleProps["data"]["customBorderRadius"];
  parentTheme: SanityCardsModuleProps["data"]["moduleLayout"]["theme"];
}

export interface LinkIconAxisType {
  horizontal: "left" | "right" | "contentEnd";
  vertical: "top" | "bottom" | "contentEnd";
}

type Props = {
  article: ArticleData;
  cardTheme: ModuleThemeType;
  parentTheme: ModuleThemeType;
  displayOptions: {
    showExcerpt: boolean;
    showArticleGroup: boolean;
  };
};
export const CardStyle: React.FC<Props> = ({
  cardTheme,
  article,
  parentTheme,
  displayOptions,
}) => {
  const url = buildUrlFromSlug({
    pageType: article.page._type,
    slug: article.slug.current,
    parent:
      article.page._type === "contentHubArticle"
        ? article.page.content.articleGroup?.parent
        : "journal",
  });

  return (
    <div id="cardStyle">
      <FullHeightAndWidthLink
        to={url}
        label={getButtonText(article)}
        component="card"
        modifier="unstyled"
      >
        <CardWrapper
          borderRadius={"12px"}
          minHeight={400}
          cardPadding={"lrg"}
          mobileCardPadding={"lrg"}
          hasLink={true}
          cardTheme={cardTheme}
          parentTheme={parentTheme}
          hasContent={true}
        >
          <Container
            height="100%"
            direction={{
              LRG: "row",
              MED: "column",
              SML: "column",
            }}
            gap="sml"
            position="relative"
            justifyContent="space-between"
          >
            <Container
              display="flex"
              direction={"column"}
              justifyContent={"flex-start"}
              alignItems={"flex-start"}
              gap="med"
            >
              <IconContainer
                verticalAlignment={"top"}
                linkIconAxis={{ horizontal: "left", vertical: "top" }}
                isLinkIconOverlappingImage={false}
                isLinkIconOverlappingImageMobile={false}
                linkIconStyle={isDarkTheme(cardTheme) ? "light" : "dark"}
                data-name="Icon"
              >
                <Icon icon="ArrowDiagonal" width={30} height={30}></Icon>
              </IconContainer>
              <Container
                maxWidth={520}
                style={{ marginTop: "auto" }}
                gap="med"
                display="flex"
                direction="column"
              >
                <TitleS>{article.page.meta.title}</TitleS>
                {displayOptions.showArticleGroup && (
                  <>
                    {article.page._type === "pressRelease" ? (
                      <span>
                        <ArticleTag theme={cardTheme} name={"Press Release"} />
                      </span>
                    ) : article.page.content.articleGroup ? (
                      <span>
                        <ArticleTag
                          theme={cardTheme}
                          name={article.page.content.articleGroup.name}
                        />
                      </span>
                    ) : null}
                  </>
                )}
                {displayOptions.showExcerpt && (
                  <BodyL>{article.page.content.excerpt}</BodyL>
                )}
              </Container>
            </Container>
            <Container
              width={{ LRG: "50%", MED: "75%", SML: "100%" }}
              maxHeight={"400px"}
            >
              <ImageBorderRadiusWrapper
                mediaStyle={{ borderRadius: "12px" }}
                height="100%"
              >
                <CardMedia
                  asset={article.page.content.articleImage.image}
                  scaleMediaUp={false}
                  inlineImageLocation={"right"}
                  mediaFillSize={"cover"}
                />
              </ImageBorderRadiusWrapper>
            </Container>
          </Container>
        </CardWrapper>
      </FullHeightAndWidthLink>
    </div>
  );
};
